<script lang="ts">
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';
  import { _ } from 'svelte-i18n';
  import type { PageData } from './$types';
  import ContentCard from '$lib/components/content/ContentCard.svelte';
  
  interface Props {
    data: PageData;
  }
  
  let { data }: Props = $props();
  
  let selectedType = $state(data.filters.type);
  let selectedCategory = $state(data.filters.category);
  
  onMount(() => {
    // Track page view
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Changelog Page View', {
        props: { 
          type: selectedType || 'all',
          category: selectedCategory || 'all',
          page: data.pagination.currentPage,
          locale: data.locale
        }
      });
    }
  });
  
  function handleTypeFilter(type: string) {
    selectedType = type === selectedType ? '' : type;
    updateFilters();
  }
  
  function handleCategoryFilter(category: string) {
    selectedCategory = category === selectedCategory ? '' : category;
    updateFilters();
  }
  
  function updateFilters() {
    const params = new URLSearchParams();
    if (selectedType) params.set('type', selectedType);
    if (selectedCategory) params.set('category', selectedCategory);
    
    const url = `/changelog${params.toString() ? '?' + params.toString() : ''}`;
    goto(url);
  }
  
  function handlePageChange(newPage: number) {
    const params = new URLSearchParams();
    if (selectedType) params.set('type', selectedType);
    if (selectedCategory) params.set('category', selectedCategory);
    if (newPage > 1) params.set('page', newPage.toString());
    
    const url = `/changelog${params.toString() ? '?' + params.toString() : ''}`;
    goto(url);
  }
  
  function clearFilters() {
    selectedType = '';
    selectedCategory = '';
    goto('/changelog');
  }
  
  function getTypeIcon(type: string): string {
    switch (type.toLowerCase()) {
      case 'feature': return '✨';
      case 'improvement': return '⚡';
      case 'bugfix': return '🐛';
      case 'security': return '🔒';
      default: return '🚀';
    }
  }
  
  function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    const localeCode = locale.split('-')[0];
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
</script>

<svelte:head>
  <title>{data.meta.title} | Heyweek</title>
  <meta name="description" content={data.meta.description} />
  <link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />
  
  <!-- RSS Feed -->
  <link rel="alternate" type="application/rss+xml" title="Heyweek Changelog RSS" href="/rss/changelog.xml" />
  
  <!-- Open Graph -->
  <meta property="og:title" content="{data.meta.title} | Heyweek" />
  <meta property="og:description" content={data.meta.description} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Twitter Card -->
  <meta name="twitter:title" content="{data.meta.title} | Heyweek" />
  <meta name="twitter:description" content={data.meta.description} />
</svelte:head>

<div class="changelog-page">
  <!-- Hero Section -->
  <section class="changelog-hero">
    <div class="container">
      <div class="changelog-hero__content">
        <h1 class="changelog-hero__title">
          {#if selectedType}
            {selectedType} {$_('changelog.updates')}
          {:else if selectedCategory}
            {selectedCategory} {$_('changelog.title')}
          {:else}
            {$_('changelog.title')}
          {/if}
        </h1>
        
        <p class="changelog-hero__subtitle">
          {#if selectedType || selectedCategory}
            {$_('changelog.filtered.subtitle', { values: { count: data.pagination.totalEntries } })}
          {:else}
            {$_('changelog.subtitle')}
          {/if}
        </p>
        
        <!-- Latest Release Badge -->
        {#if data.latestRelease && !selectedType && !selectedCategory && data.pagination.currentPage === 1}
          <div class="latest-release">
            <span class="latest-release__badge">{$_('changelog.latest')}</span>
            <span class="latest-release__version">{data.latestRelease.version}</span>
            <span class="latest-release__date">
              {formatDate(data.latestRelease.date, data.locale)}
            </span>
          </div>
        {/if}
      </div>
    </div>
  </section>
  
  <!-- Content -->
  <section class="changelog-content">
    <div class="container">
      <div class="changelog-content__layout">
        <!-- Sidebar Filters -->
        <aside class="changelog-sidebar">
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('changelog.filters.types')}</h3>
            <div class="filter-section__options">
              {#each data.types as type}
                <button
                  class="filter-option {selectedType === type ? 'filter-option--active' : ''}"
                  onclick={() => handleTypeFilter(type)}
                >
                  <span class="filter-option__icon">{getTypeIcon(type)}</span>
                  {type}
                </button>
              {/each}
            </div>
          </div>
          
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('changelog.filters.categories')}</h3>
            <div class="filter-section__options">
              {#each data.categories as category}
                <button
                  class="filter-option {selectedCategory === category ? 'filter-option--active' : ''}"
                  onclick={() => handleCategoryFilter(category)}
                >
                  {category}
                </button>
              {/each}
            </div>
          </div>
          
          {#if selectedType || selectedCategory}
            <button class="clear-filters" onclick={clearFilters}>
              {$_('changelog.filters.clear')}
            </button>
          {/if}
        </aside>
        
        <!-- Main Content -->
        <main class="changelog-main">
          {#if data.entries.length > 0}
            <div class="changelog-entries">
              <div class="changelog-entries__header">
                <h2 class="changelog-entries__title">
                  {#if selectedType || selectedCategory}
                    {$_('changelog.filtered.results', { values: { count: data.pagination.totalEntries } })}
                  {:else}
                    {$_('changelog.recent.title')}
                  {/if}
                </h2>
                <p class="changelog-entries__count">
                  {$_('changelog.showing', { 
                    values: { 
                      start: (data.pagination.currentPage - 1) * 10 + 1,
                      end: Math.min(data.pagination.currentPage * 10, data.pagination.totalEntries),
                      total: data.pagination.totalEntries
                    }
                  })}
                </p>
              </div>
              
              <div class="changelog-entries__list">
                {#each data.entries as entry}
                  <a class="changelog-entry" href="/changelog/{entry.slug}">
                    <div class="changelog-entry__header">
                      <div class="changelog-entry__meta">
                        <span class="changelog-entry__type">
                          <span class="changelog-entry__type-icon">{getTypeIcon(entry.type)}</span>
                          {entry.type}
                        </span>
                        <span class="changelog-entry__version">{entry.version}</span>
                        <time class="changelog-entry__date" datetime={entry.date}>
                          {formatDate(entry.date, data.locale)}
                        </time>
                      </div>
                      <span class="changelog-entry__category">{entry.category}</span>
                    </div>
                    
                    <h3 class="changelog-entry__title">{entry.title}</h3>
                    <p class="changelog-entry__excerpt">{entry.excerpt}</p>
                    
                    {#if entry.tags.length > 0}
                      <div class="changelog-entry__tags">
                        {#each entry.tags as tag}
                          <span class="changelog-entry__tag">#{tag}</span>
                        {/each}
                      </div>
                    {/if}
                  </a>
                {/each}
              </div>
              
              <!-- Pagination -->
              {#if data.pagination.totalPages > 1}
                <div class="pagination">
                  <button
                    class="pagination__button"
                    disabled={!data.pagination.hasPrevPage}
                    onclick={() => handlePageChange(data.pagination.currentPage - 1)}
                  >
                    ← {$_('pagination.previous')}
                  </button>
                  
                  <div class="pagination__info">
                    {$_('pagination.pageOf', { 
                      values: { 
                        current: data.pagination.currentPage, 
                        total: data.pagination.totalPages 
                      }
                    })}
                  </div>
                  
                  <button
                    class="pagination__button"
                    disabled={!data.pagination.hasNextPage}
                    onclick={() => handlePageChange(data.pagination.currentPage + 1)}
                  >
                    {$_('pagination.next')} →
                  </button>
                </div>
              {/if}
            </div>
          {:else}
            <div class="changelog-empty">
              <div class="changelog-empty__icon">🚀</div>
              <h3 class="changelog-empty__title">{$_('changelog.empty.title')}</h3>
              <p class="changelog-empty__description">{$_('changelog.empty.description')}</p>
              {#if selectedType || selectedCategory}
                <button class="changelog-empty__action" onclick={clearFilters}>
                  {$_('changelog.empty.clearFilters')}
                </button>
              {/if}
            </div>
          {/if}
        </main>
      </div>
    </div>
  </section>
</div>

<style>
  .changelog-page {
    min-height: 100vh;
  }
  
  .changelog-hero {
    background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
    padding: var(--space-12) 0 var(--space-8);
    border-bottom: 1px solid var(--border-light);
  }
  
  .changelog-hero__content {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .changelog-hero__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
    line-height: 1.2;
  }
  
  .changelog-hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    margin-bottom: var(--space-6);
    line-height: 1.5;
  }
  
  .latest-release {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--brand-primary-light);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-full);
    font-size: var(--font-size-sm);
  }
  
  .latest-release__badge {
    background: var(--brand-primary);
    color: white;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .latest-release__version {
    font-weight: var(--font-weight-semibold);
    color: var(--brand-primary-dark);
  }
  
  .latest-release__date {
    color: var(--text-secondary);
  }
  
  .changelog-content {
    padding: var(--space-8) 0;
  }
  
  .changelog-content__layout {
    display: grid;
    grid-template-columns: 250px 1fr;
    gap: var(--space-8);
    align-items: start;
  }
  
  .changelog-sidebar {
    position: sticky;
    top: var(--space-8);
  }
  
  .filter-section {
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
  }
  
  .filter-section__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
  }
  
  .filter-section__options {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .filter-option {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: none;
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-all);
    text-align: left;
  }
  
  .filter-option:hover,
  .filter-option--active {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  .filter-option__icon {
    font-size: var(--font-size-base);
  }
  
  .clear-filters {
    width: 100%;
    padding: var(--space-3);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-colors);
  }
  
  .clear-filters:hover {
    background: var(--bg-primary);
  }
  
  .changelog-entries__header {
    margin-bottom: var(--space-6);
  }
  
  .changelog-entries__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }
  
  .changelog-entries__count {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .changelog-entries__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    margin-bottom: var(--space-8);
  }
  
  .changelog-entry {
    display: block;
    text-decoration: none;
    color: inherit;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    transition: var(--transition-all);
  }
  
  .changelog-entry:hover {
    border-color: var(--border-medium);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .changelog-entry__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-4);
  }
  
  .changelog-entry__meta {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  
  .changelog-entry__type {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .changelog-entry__type-icon {
    font-size: var(--font-size-sm);
  }
  
  .changelog-entry__version {
    background: var(--brand-primary);
    color: white;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
  }
  
  .changelog-entry__date {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }
  
  .changelog-entry__category {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-full);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
  }
  
  .changelog-entry__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
    line-height: 1.3;
  }
  
  .changelog-entry__excerpt {
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: var(--space-4);
  }
  
  .changelog-entry__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
  }
  
  .changelog-entry__tag {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-secondary);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-light);
  }
  
  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-6) 0;
    border-top: 1px solid var(--border-light);
  }
  
  .pagination__button {
    padding: var(--space-3) var(--space-6);
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-all);
  }
  
  .pagination__button:hover:not(:disabled) {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  .pagination__button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination__info {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .changelog-empty {
    text-align: center;
    padding: var(--space-12) var(--space-4);
  }
  
  .changelog-empty__icon {
    font-size: 4rem;
    margin-bottom: var(--space-4);
    opacity: 0.6;
  }
  
  .changelog-empty__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }
  
  .changelog-empty__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: var(--space-6);
  }
  
  .changelog-empty__action {
    padding: var(--space-3) var(--space-6);
    background: var(--brand-primary);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-colors);
  }
  
  .changelog-empty__action:hover {
    background: var(--brand-primary-dark);
  }
  
  @media (max-width: 1024px) {
    .changelog-content__layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
    
    .changelog-sidebar {
      position: static;
      order: -1;
    }
    
    .filter-section {
      padding: var(--space-4);
    }
  }
  
  @media (max-width: 768px) {
    .changelog-hero__title {
      font-size: var(--font-size-xl);
    }
    
    .changelog-hero__subtitle {
      font-size: var(--font-size-base);
    }
    
    .changelog-entry__header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-2);
    }
    
    .changelog-entry__meta {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-1);
    }
    
    .pagination {
      flex-direction: column;
      gap: var(--space-3);
    }
  }
</style>
